import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  Camera,
  User,
  Mountains,
  TShirt,
  Buildings,
  PawPrint,
  Package,
  Moon,
  MagnifyingGlassPlus,
  Lightning,
  UsersThree,
  ForkKnife,
  CaretDown,
  CaretUp,
  Star,
  CircleHalf,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const difficultyColors = {
  beginner: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/20" },
  intermediate: { bg: "bg-[#F5A524]/10", text: "text-[#F5A524]", border: "border-[#F5A524]/20" },
  advanced: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
};

const projects = {
  en: {
    page_title: "Photography Projects",
    page_sub: "Hands-on shooting assignments to build your skills. Pick a project, grab your camera, and go shoot!",
    difficulty: "Difficulty",
    objective: "Objective",
    settings_hint: "Suggested Settings",
    tips: "Pro Tips",
    deliverables: "Deliverables",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    items: [
      {
        id: "food",
        icon: "ForkKnife",
        title: "Food Photography",
        desc: "Photograph a meal or dish with props and natural light to create an appetizing, magazine-quality image.",
        difficulty: "beginner",
        objective: "Create 5 images of a plated dish from different angles (overhead, 45-degree, and eye-level) using only window light.",
        settings: "ISO 200-400 | f/2.8-f/4 for shallow DOF | 1/125 sec | White balance: Daylight or Cloudy",
        tips: [
          "Shoot near a large window — soft, directional light is the secret.",
          "Use a white board or napkin as a reflector on the shadow side.",
          "Style the plate before shooting: wipe drips, add fresh herbs, drizzle sauce.",
          "Try a 45-degree angle first — it's the most natural 'eating' perspective.",
          "Include contextual props (cutlery, linen, ingredients) but don't clutter.",
        ],
        deliverables: "5 edited images: 1 overhead (flat lay), 2 at 45 degrees, 1 eye-level, 1 detail close-up.",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Portrait Photography",
        desc: "Capture three distinct portrait styles of the same person — headshot, environmental, and creative.",
        difficulty: "beginner",
        objective: "Produce 3 portraits showing different moods using natural light and simple backgrounds.",
        settings: "ISO 100-400 | f/1.8-f/2.8 for background blur | 1/200 sec | 50-85mm focal length",
        tips: [
          "Shoot during golden hour or in open shade for soft, flattering light.",
          "Focus on the eyes — always. Use single-point AF on the nearest eye.",
          "Direct your subject: have them look slightly off-camera for a candid feel.",
          "For headshots, keep the background simple and uncluttered.",
          "For environmental portraits, include surroundings that tell a story about the person.",
        ],
        deliverables: "3 edited portraits: 1 tight headshot, 1 environmental (wider, subject in context), 1 creative (unusual angle, light, or mood).",
      },
      {
        id: "landscape",
        icon: "Mountains",
        title: "Landscape Photography",
        desc: "Capture a golden hour landscape that demonstrates depth, leading lines, and proper exposure.",
        difficulty: "beginner",
        objective: "Shoot 5 landscape images during golden hour showing foreground, midground, and background elements.",
        settings: "ISO 100 | f/8-f/11 for sharpness | Tripod recommended | 1/30-1/125 sec | Wide-angle (16-35mm)",
        tips: [
          "Arrive 30 minutes before golden hour to scout your composition.",
          "Use f/8-f/11 for maximum sharpness across the frame.",
          "Include a strong foreground element (rocks, flowers, water) for depth.",
          "Place the horizon on the upper or lower third — never dead center.",
          "Bracket exposures (+/- 1 stop) in high-contrast scenes.",
        ],
        deliverables: "5 edited landscapes: 2 wide views, 1 with leading lines, 1 with strong foreground, 1 panoramic or vertical.",
      },
      {
        id: "fashion",
        icon: "TShirt",
        title: "Fashion & Street Style",
        desc: "Photograph 5 outfit looks with urban backdrops that highlight both clothing and environment.",
        difficulty: "intermediate",
        objective: "Create a mini lookbook: 5 full-body fashion shots with varied poses, angles, and urban locations.",
        settings: "ISO 100-400 | f/2-f/4 | 1/250 sec | 50-85mm focal length",
        tips: [
          "Scout colorful walls, stairways, or textured surfaces as backdrops.",
          "Shoot at eye level or slightly below to make your model look taller.",
          "Direct movement: walking, turning, adjusting an accessory adds energy.",
          "Ensure the clothing is the hero — background should complement, not compete.",
          "Overcast skies are your friend: even, soft light with no harsh shadows.",
        ],
        deliverables: "5 edited images: mix of full-body and 3/4 shots, at least 2 different locations.",
      },
      {
        id: "street",
        icon: "Camera",
        title: "Street Photography",
        desc: "Document candid moments of everyday life in a public space without staging or directing subjects.",
        difficulty: "intermediate",
        objective: "Shoot 10 candid images in 1 hour that capture emotion, story, or interesting juxtaposition.",
        settings: "ISO 400-800 | f/5.6-f/8 | 1/250 sec | 35-50mm focal length | Zone focus",
        tips: [
          "Pre-set exposure and focus to a zone distance (3-5 meters) so you can shoot fast.",
          "Look for contrasts: old/new, light/shadow, movement/stillness.",
          "Be patient — wait at an interesting location for a moment to unfold.",
          "Shoot from the hip for discreet candid moments.",
          "Respect people's space. If someone is uncomfortable, move on.",
        ],
        deliverables: "10 unedited + 5 selects with basic editing. No staging — all must be candid.",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Architecture Photography",
        desc: "Capture building lines, symmetry, and geometric patterns with precise framing.",
        difficulty: "intermediate",
        objective: "Photograph 5 buildings or structures emphasizing lines, symmetry, and geometric repetition.",
        settings: "ISO 100-200 | f/8-f/11 | Tripod recommended | Wide-angle (16-24mm) or telephoto for compression",
        tips: [
          "Level your camera — tilted verticals are the #1 beginner mistake.",
          "Use a wide-angle lens close up for dramatic perspective, or telephoto from far for compression.",
          "Shoot at blue hour for dramatic sky colors behind lit buildings.",
          "Look for repeating patterns: windows, columns, staircases.",
          "Include a human figure for scale in at least one shot.",
        ],
        deliverables: "5 edited images: 1 symmetrical, 1 with leading lines, 1 detail/pattern, 1 with human scale, 1 at blue hour or dramatic light.",
      },
      {
        id: "wildlife",
        icon: "PawPrint",
        title: "Wildlife & Nature",
        desc: "Photograph animals or insects in their natural habitat with patience and fast reflexes.",
        difficulty: "advanced",
        objective: "Capture 5 images of animals or insects showing behavior, detail, or interaction with environment.",
        settings: "ISO 400-1600 | f/4-f/5.6 | 1/500+ sec for action | 200-400mm telephoto or macro",
        tips: [
          "Move slowly and quietly. Let the animal come to you.",
          "Use continuous autofocus (AF-C) to track moving subjects.",
          "Get down to the animal's eye level for a more intimate perspective.",
          "Shoot in burst mode — behavior happens in split seconds.",
          "Early morning and late afternoon are when animals are most active.",
        ],
        deliverables: "5 edited images: 1 portrait (eye detail), 1 action/behavior, 1 in environment, 1 macro detail, 1 creative (silhouette or backlit).",
      },
      {
        id: "product",
        icon: "Package",
        title: "Product Photography",
        desc: "Shoot clean product images suitable for an online store using simple DIY lighting.",
        difficulty: "beginner",
        objective: "Create 5 product images on a clean background (white or dark) with even, shadow-free lighting.",
        settings: "ISO 100-200 | f/5.6-f/8 | Tripod | 1/60-1/125 sec | 50-100mm",
        tips: [
          "Use a white poster board curved into an \"infinity curve\" for a seamless background.",
          "Two desk lamps with white paper diffusers = affordable studio lighting.",
          "Shoot from multiple angles: front, 45-degree, top-down, detail close-up.",
          "Keep the product clean: fingerprints and dust show in photos.",
          "For reflective products, use a light tent or surround with white cards.",
        ],
        deliverables: "5 edited images per product: front, angle, top, detail, lifestyle context shot.",
      },
      {
        id: "night",
        icon: "Moon",
        title: "Night & Long Exposure",
        desc: "Create dramatic nighttime images using long exposures, light trails, and creative techniques.",
        difficulty: "advanced",
        objective: "Produce 5 night images: light trails, star trails or starry sky, light painting, city at blue hour, and a creative long exposure.",
        settings: "ISO 100-800 | f/8-f/16 for trails, f/2.8 for stars | 10-30 sec (or bulb) | Tripod essential",
        tips: [
          "Use a tripod and remote shutter release (or 2-second timer) to avoid shake.",
          "For light trails: 10-30 seconds at f/8, ISO 100 near a busy road.",
          "For stars: f/2.8, ISO 1600-3200, 15-25 seconds (use 500 Rule: 500/focal length = max seconds).",
          "Shoot at blue hour (20-40 min after sunset) for the best sky-to-city contrast.",
          "Light painting: use a flashlight during a long exposure to 'paint' light onto your subject.",
        ],
        deliverables: "5 edited images: 1 light trail, 1 starry sky, 1 light painting, 1 blue hour cityscape, 1 creative.",
      },
      {
        id: "macro",
        icon: "MagnifyingGlassPlus",
        title: "Macro Photography",
        desc: "Explore the miniature world of extreme close-ups revealing textures and details invisible to the naked eye.",
        difficulty: "intermediate",
        objective: "Capture 5 macro images of different subjects showing texture, pattern, and extreme detail.",
        settings: "ISO 200-400 | f/8-f/11 for more DOF | 1/200+ sec (or tripod) | Macro lens or extension tubes",
        tips: [
          "Depth of field is razor-thin in macro — use f/8 or smaller and focus stack if possible.",
          "Use manual focus and rock your body forward/backward to nail the focus plane.",
          "Overcast days or shade provide even, diffused light that reveals texture.",
          "Flowers, insects, water drops, food textures, and fabric patterns are great subjects.",
          "A tripod with a ball head is nearly essential for sharp macro work.",
        ],
        deliverables: "5 edited macro images: 1 insect/bug, 1 flower detail, 1 texture (fabric/wood/metal), 1 water drop, 1 food close-up.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Sports & Action",
        desc: "Freeze fast-moving subjects with high shutter speeds and continuous tracking autofocus.",
        difficulty: "advanced",
        objective: "Capture 5 action shots that freeze peak motion and convey energy and athleticism.",
        settings: "ISO 400-1600 | f/2.8-f/4 | 1/1000+ sec | AF-C continuous tracking | 70-200mm",
        tips: [
          "Use Shutter Priority (Tv/S) mode: set 1/1000+ and let the camera choose aperture.",
          "Continuous AF (AF-C / AI Servo) is non-negotiable for moving subjects.",
          "Anticipate the peak moment: a basketball player at the top of their jump, a runner crossing the finish.",
          "Pan with the subject for motion blur in the background but a sharp subject.",
          "Shoot in burst mode — capture 5-10 frames per second to catch the decisive moment.",
        ],
        deliverables: "5 edited action images: 1 frozen peak moment, 1 panning shot (blurred background), 1 pre-game preparation, 1 celebration/emotion, 1 wide establishing shot.",
      },
      {
        id: "event",
        icon: "UsersThree",
        title: "Event Photography",
        desc: "Document a social gathering by capturing key moments, candid emotions, and group shots.",
        difficulty: "intermediate",
        objective: "Cover a social event (party, family gathering, school event) with a variety of shots that tell the story.",
        settings: "ISO 800-3200 | f/2.8-f/4 | 1/125-1/250 sec | Flash bounce if indoors | 24-70mm",
        tips: [
          "Arrive early to scout lighting and find good angles before guests arrive.",
          "Capture the three phases: setup/anticipation, key moments, candid reactions.",
          "Bounce your flash off the ceiling for soft, natural-looking indoor light.",
          "Get both wide shots (environment/atmosphere) and tight shots (emotion/detail).",
          "Keep moving — don't stay in one spot the entire event.",
        ],
        deliverables: "10 edited images: 2 wide venue/atmosphere, 3 candid moments, 2 posed groups, 2 detail/decor shots, 1 creative (silhouette, reflection, or motion blur).",
      },
      {
        id: "blackwhite",
        icon: "CircleHalf",
        title: "Black & White Photography",
        desc: "Shoot a series of frames using only monochrome — focus on tonal range, contrast, shadow shapes, and texture instead of color.",
        difficulty: "intermediate",
        objective: "Create a cohesive set of 8 monochrome images where light, contrast, and texture — not color — carry the entire frame.",
        settings: "ISO 100-800 | Shoot RAW + set camera Picture Style/Profile to Monochrome for a live B&W preview | Meter for the highlights | Any focal length",
        tips: [
          "Train your eye to see tones, not colors: squint at the scene to judge how light and dark areas separate.",
          "Hunt for contrast and texture — side light at golden hour reveals shape, grit, and form that color hides.",
          "Use the camera's Monochrome Picture Style so the rear screen previews B&W, but keep shooting RAW to retain full data for conversion.",
          "Shadows are a subject: let blacks go truly black and use negative space deliberately.",
          "Color filters (or their digital equivalents) change tonal relationships — a red filter darkens blue skies for dramatic clouds.",
        ],
        deliverables: "8 edited monochrome images: 2 high-contrast, 2 low-key (shadow-dominant), 2 texture/detail studies, 1 high-key (bright), 1 environmental portrait.",
      },
    ],
  },
  es: {
    page_title: "Proyectos Fotograficos",
    page_sub: "Asignaciones practicas de fotografia para desarrollar tus habilidades. Elige un proyecto, toma tu camara y sal a disparar!",
    difficulty: "Dificultad",
    objective: "Objetivo",
    settings_hint: "Ajustes Sugeridos",
    tips: "Consejos Pro",
    deliverables: "Entregables",
    beginner: "Principiante",
    intermediate: "Intermedio",
    advanced: "Avanzado",
    items: [
      {
        id: "food",
        icon: "ForkKnife",
        title: "Fotografia de Alimentos",
        desc: "Fotografiar un plato con accesorios y luz natural para crear una imagen apetitosa de calidad editorial.",
        difficulty: "beginner",
        objective: "Crear 5 imagenes de un plato desde diferentes angulos (cenital, 45 grados y nivel de ojo) usando solo luz de ventana.",
        settings: "ISO 200-400 | f/2.8-f/4 para poca profundidad | 1/125 seg | Balance de blancos: Luz de Dia o Nublado",
        tips: [
          "Dispara cerca de una ventana grande — la luz suave y direccional es el secreto.",
          "Usa una cartulina blanca o servilleta como reflector en el lado de la sombra.",
          "Prepara el plato antes de disparar: limpia gotas, anade hierbas frescas, rociar salsa.",
          "Prueba un angulo de 45 grados primero — es la perspectiva mas natural para 'comer'.",
          "Incluye accesorios contextuales (cubiertos, mantel, ingredientes) pero no satures.",
        ],
        deliverables: "5 imagenes editadas: 1 cenital (flat lay), 2 a 45 grados, 1 a nivel de ojo, 1 detalle de cerca.",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Fotografia de Retratos",
        desc: "Captura tres estilos distintos de retrato de la misma persona — primer plano, ambiental y creativo.",
        difficulty: "beginner",
        objective: "Producir 3 retratos mostrando diferentes estados de animo usando luz natural y fondos simples.",
        settings: "ISO 100-400 | f/1.8-f/2.8 para desenfoque | 1/200 seg | 50-85mm distancia focal",
        tips: [
          "Dispara durante la hora dorada o en sombra abierta para luz suave y favorecedora.",
          "Enfoca en los ojos — siempre. Usa AF de punto unico en el ojo mas cercano.",
          "Dirige a tu sujeto: que mire ligeramente fuera de camara para un aire natural.",
          "Para primeros planos, mantener el fondo simple y limpio.",
          "Para retratos ambientales, incluye el entorno que cuente una historia sobre la persona.",
        ],
        deliverables: "3 retratos editados: 1 primer plano cerrado, 1 ambiental (mas amplio, sujeto en contexto), 1 creativo (angulo, luz o estado de animo inusual).",
      },
      {
        id: "landscape",
        icon: "Mountains",
        title: "Fotografia de Paisaje",
        desc: "Capturar un paisaje en hora dorada que demuestre profundidad, lineas guia y exposicion correcta.",
        difficulty: "beginner",
        objective: "Disparar 5 imagenes de paisaje durante hora dorada mostrando primer plano, plano medio y fondo.",
        settings: "ISO 100 | f/8-f/11 para nitidez | Tripode recomendado | 1/30-1/125 seg | Gran angular (16-35mm)",
        tips: [
          "Llega 30 minutos antes de la hora dorada para explorar tu composicion.",
          "Usa f/8-f/11 para maxima nitidez en todo el encuadre.",
          "Incluye un elemento fuerte en primer plano (rocas, flores, agua) para dar profundidad.",
          "Coloca el horizonte en el tercio superior o inferior — nunca en el centro exacto.",
          "Bracketing de exposicion (+/- 1 paso) en escenas de alto contraste.",
        ],
        deliverables: "5 paisajes editados: 2 vistas amplias, 1 con lineas guia, 1 con primer plano fuerte, 1 panoramica o vertical.",
      },
      {
        id: "fashion",
        icon: "TShirt",
        title: "Moda y Estilo Urbano",
        desc: "Fotografiar 5 looks de outfits con fondos urbanos que destaquen tanto la ropa como el entorno.",
        difficulty: "intermediate",
        objective: "Crear un mini lookbook: 5 fotos de moda de cuerpo completo con poses variadas, angulos y locaciones urbanas.",
        settings: "ISO 100-400 | f/2-f/4 | 1/250 seg | 50-85mm distancia focal",
        tips: [
          "Busca paredes coloridas, escaleras o superficies texturizadas como fondos.",
          "Dispara a nivel de ojo o ligeramente desde abajo para que el modelo luzca mas alto.",
          "Dirige movimiento: caminar, girar, ajustar un accesorio anade energia.",
          "Asegurate de que la ropa sea la protagonista — el fondo debe complementar, no competir.",
          "Los cielos nublados son tu amigo: luz uniforme y suave sin sombras duras.",
        ],
        deliverables: "5 imagenes editadas: mezcla de cuerpo completo y 3/4, al menos 2 locaciones diferentes.",
      },
      {
        id: "street",
        icon: "Camera",
        title: "Fotografia Callejera",
        desc: "Documentar momentos espontaneos de la vida cotidiana en un espacio publico sin escenificar ni dirigir sujetos.",
        difficulty: "intermediate",
        objective: "Disparar 10 imagenes espontaneas en 1 hora que capturen emocion, historia o yuxtaposicion interesante.",
        settings: "ISO 400-800 | f/5.6-f/8 | 1/250 seg | 35-50mm distancia focal | Enfoque de zona",
        tips: [
          "Pre-configura exposicion y enfoque a una distancia de zona (3-5 metros) para disparar rapido.",
          "Busca contrastes: viejo/nuevo, luz/sombra, movimiento/quietud.",
          "Se paciente — espera en un lugar interesante a que un momento se desarrolle.",
          "Dispara desde la cadera para momentos espontaneos discretos.",
          "Respeta el espacio de las personas. Si alguien esta incomodo, sigue adelante.",
        ],
        deliverables: "10 sin editar + 5 seleccionadas con edicion basica. Sin escenificacion — todas deben ser espontaneas.",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Fotografia de Arquitectura",
        desc: "Capturar lineas de edificios, simetria y patrones geometricos con encuadre preciso.",
        difficulty: "intermediate",
        objective: "Fotografiar 5 edificios o estructuras enfatizando lineas, simetria y repeticion geometrica.",
        settings: "ISO 100-200 | f/8-f/11 | Tripode recomendado | Gran angular (16-24mm) o teleobjetivo para compresion",
        tips: [
          "Nivela tu camara — las verticales inclinadas son el error #1 de principiante.",
          "Usa gran angular de cerca para perspectiva dramatica, o tele de lejos para compresion.",
          "Dispara en hora azul para colores dramaticos de cielo detras de edificios iluminados.",
          "Busca patrones repetitivos: ventanas, columnas, escaleras.",
          "Incluye una figura humana para escala en al menos una toma.",
        ],
        deliverables: "5 imagenes editadas: 1 simetrica, 1 con lineas guia, 1 detalle/patron, 1 con escala humana, 1 en hora azul o luz dramatica.",
      },
      {
        id: "wildlife",
        icon: "PawPrint",
        title: "Fauna y Naturaleza",
        desc: "Fotografiar animales o insectos en su habitat natural con paciencia y reflejos rapidos.",
        difficulty: "advanced",
        objective: "Capturar 5 imagenes de animales o insectos mostrando comportamiento, detalle o interaccion con el entorno.",
        settings: "ISO 400-1600 | f/4-f/5.6 | 1/500+ seg para accion | 200-400mm teleobjetivo o macro",
        tips: [
          "Muevete lenta y silenciosamente. Deja que el animal venga a ti.",
          "Usa enfoque continuo (AF-C) para rastrear sujetos en movimiento.",
          "Baja al nivel de los ojos del animal para una perspectiva mas intima.",
          "Dispara en modo rafaga — el comportamiento ocurre en fracciones de segundo.",
          "Temprano en la manana y al final de la tarde es cuando los animales estan mas activos.",
        ],
        deliverables: "5 imagenes editadas: 1 retrato (detalle de ojo), 1 accion/comportamiento, 1 en entorno, 1 detalle macro, 1 creativa (silueta o contraluz).",
      },
      {
        id: "product",
        icon: "Package",
        title: "Fotografia de Producto",
        desc: "Disparar imagenes limpias de productos adecuadas para tienda online usando iluminacion casera simple.",
        difficulty: "beginner",
        objective: "Crear 5 imagenes de producto sobre fondo limpio (blanco u oscuro) con iluminacion uniforme sin sombras.",
        settings: "ISO 100-200 | f/5.6-f/8 | Tripode | 1/60-1/125 seg | 50-100mm",
        tips: [
          "Usa una cartulina blanca curvada en forma de 'curva infinita' para un fondo sin costuras.",
          "Dos lamparas de escritorio con difusores de papel blanco = iluminacion de estudio accesible.",
          "Dispara desde multiples angulos: frente, 45 grados, cenital, detalle de cerca.",
          "Mantener el producto limpio: huellas y polvo se notan en las fotos.",
          "Para productos reflectantes, usa una carpa de luz o rodea con cartulinas blancas.",
        ],
        deliverables: "5 imagenes editadas por producto: frente, angulo, cenital, detalle, toma de contexto lifestyle.",
      },
      {
        id: "night",
        icon: "Moon",
        title: "Noche y Larga Exposicion",
        desc: "Crear imagenes nocturnas dramaticas usando largas exposiciones, estelas de luz y tecnicas creativas.",
        difficulty: "advanced",
        objective: "Producir 5 imagenes nocturnas: estelas de luz, estrellas, light painting, ciudad en hora azul, y una larga exposicion creativa.",
        settings: "ISO 100-800 | f/8-f/16 para estelas, f/2.8 para estrellas | 10-30 seg (o bulb) | Tripode esencial",
        tips: [
          "Usa tripode y disparador remoto (o temporizador de 2 segundos) para evitar vibracion.",
          "Para estelas de luz: 10-30 segundos a f/8, ISO 100 cerca de una calle transitada.",
          "Para estrellas: f/2.8, ISO 1600-3200, 15-25 segundos (usa Regla 500: 500/distancia focal = segundos maximos).",
          "Dispara en hora azul (20-40 min despues del atardecer) para el mejor contraste cielo-ciudad.",
          "Light painting: usa una linterna durante una larga exposicion para 'pintar' luz sobre tu sujeto.",
        ],
        deliverables: "5 imagenes editadas: 1 estela de luz, 1 cielo estrellado, 1 light painting, 1 paisaje urbano en hora azul, 1 creativa.",
      },
      {
        id: "macro",
        icon: "MagnifyingGlassPlus",
        title: "Fotografia Macro",
        desc: "Explorar el mundo miniatura de acercamientos extremos revelando texturas y detalles invisibles a simple vista.",
        difficulty: "intermediate",
        objective: "Capturar 5 imagenes macro de diferentes sujetos mostrando textura, patron y detalle extremo.",
        settings: "ISO 200-400 | f/8-f/11 para mas profundidad | 1/200+ seg (o tripode) | Lente macro o tubos de extension",
        tips: [
          "La profundidad de campo es minima en macro — usa f/8 o menor y apila enfoque si es posible.",
          "Usa enfoque manual y mueve tu cuerpo adelante/atras para clavar el plano de enfoque.",
          "Dias nublados o sombra proporcionan luz uniforme y difusa que revela textura.",
          "Flores, insectos, gotas de agua, texturas de alimentos y patrones de tela son grandes sujetos.",
          "Un tripode con cabezal de bola es casi esencial para trabajo macro nitido.",
        ],
        deliverables: "5 imagenes macro editadas: 1 insecto, 1 detalle de flor, 1 textura (tela/madera/metal), 1 gota de agua, 1 acercamiento de comida.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Deportes y Accion",
        desc: "Congelar sujetos en rapido movimiento con altas velocidades de obturador y enfoque continuo.",
        difficulty: "advanced",
        objective: "Capturar 5 tomas de accion que congelen el momento cumbre y transmitan energia y atletismo.",
        settings: "ISO 400-1600 | f/2.8-f/4 | 1/1000+ seg | AF-C rastreo continuo | 70-200mm",
        tips: [
          "Usa Prioridad de Velocidad (Tv/S): configura 1/1000+ y deja que la camara elija apertura.",
          "AF Continuo (AF-C / AI Servo) es innegociable para sujetos en movimiento.",
          "Anticipa el momento cumbre: un jugador de basketball en el punto mas alto de su salto.",
          "Haz paneo con el sujeto para desenfoque de movimiento en el fondo pero sujeto nitido.",
          "Dispara en modo rafaga — captura 5-10 cuadros por segundo para atrapar el momento decisivo.",
        ],
        deliverables: "5 imagenes de accion editadas: 1 momento cumbre congelado, 1 paneo (fondo borroso), 1 preparacion, 1 celebracion/emocion, 1 toma amplia de establecimiento.",
      },
      {
        id: "event",
        icon: "UsersThree",
        title: "Fotografia de Eventos",
        desc: "Documentar una reunion social capturando momentos clave, emociones espontaneas y fotos grupales.",
        difficulty: "intermediate",
        objective: "Cubrir un evento social (fiesta, reunion familiar, evento escolar) con variedad de tomas que cuenten la historia.",
        settings: "ISO 800-3200 | f/2.8-f/4 | 1/125-1/250 seg | Flash rebotado en interiores | 24-70mm",
        tips: [
          "Llega temprano para explorar iluminacion y encontrar buenos angulos antes de que lleguen los invitados.",
          "Captura las tres fases: preparacion/anticipacion, momentos clave, reacciones espontaneas.",
          "Rebota tu flash en el techo para luz suave y natural en interiores.",
          "Obtener tomas amplias (ambiente/atmosfera) y cerradas (emocion/detalle).",
          "Sigue moviéndote — no te quedes en un solo lugar todo el evento.",
        ],
        deliverables: "10 imagenes editadas: 2 amplias de ambiente, 3 momentos espontaneos, 2 grupos posados, 2 detalle/decoracion, 1 creativa (silueta, reflejo o movimiento).",
      },
      {
        id: "blackwhite",
        icon: "CircleHalf",
        title: "Fotografia en Blanco y Negro",
        desc: "Dispara una serie de cuadros usando solo monocromo — enfocate en el rango tonal, el contraste, las formas de las sombras y la textura en lugar del color.",
        difficulty: "intermediate",
        objective: "Crear un conjunto coherente de 8 imagenes monocromas donde la luz, el contraste y la textura — no el color — sostengan todo el encuadre.",
        settings: "ISO 100-800 | Dispara RAW + configura el Estilo de Imagen/Perfil de la camara en Monocromo para una vista previa B/N | Mide para las altas luces | Cualquier distancia focal",
        tips: [
          "Entrena tu ojo para ver tonos, no colores: entrecierra los ojos ante la escena para juzgar como se separan las zonas claras y oscuras.",
          "Busca contraste y textura — la luz lateral en hora dorada revela forma, aspereza y volumen que el color oculta.",
          "Usa el Estilo de Imagen Monocromo para que la pantalla muestre B/N, pero sigue disparando RAW para conservar todos los datos en la conversion.",
          "Las sombras son un sujeto: deja que los negros sean realmente negros y usa el espacio negativo a proposito.",
          "Los filtros de color (o su equivalente digital) cambian las relaciones tonales — un filtro rojo oscurece los cielos azules para nubes dramaticas.",
        ],
        deliverables: "8 imagenes monocromas editadas: 2 de alto contraste, 2 de clave baja (dominadas por sombra), 2 estudios de textura/detalle, 1 de clave alta (luminosa), 1 retrato ambiental.",
      },
    ],
  },
};

const iconMap = {
  ForkKnife,
  User,
  Mountains,
  TShirt,
  Camera,
  Buildings,
  PawPrint,
  Package,
  Moon,
  MagnifyingGlassPlus,
  Lightning,
  UsersThree,
  CircleHalf,
};

export default function ProjectsPage() {
  const { lang } = useTranslation();
  const c = projects[lang] || projects.en;
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="projects-page">
      <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
        <motion.div variants={item}>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
            {c.page_title}
          </h1>
          <p className="text-[#71717A] text-base">{c.page_sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-4">
          {c.items.map((proj) => {
            const Icon = iconMap[proj.icon] || Camera;
            const dc = difficultyColors[proj.difficulty];
            const isOpen = expanded === proj.id;

            return (
              <motion.div
                key={proj.id}
                variants={item}
                className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden hover:border-white/15 transition-colors"
                data-testid={`project-${proj.id}`}
              >
                <button
                  onClick={() => toggle(proj.id)}
                  className="w-full flex items-center gap-4 p-5 text-left"
                  data-testid={`project-toggle-${proj.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 flex items-center justify-center shrink-0">
                    <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-['Outfit'] text-white font-medium">{proj.title}</h3>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${dc.bg} ${dc.text} ${dc.border} border`}>
                        {c[proj.difficulty]}
                      </span>
                    </div>
                    <p className="text-[#71717A] text-sm truncate">{proj.desc}</p>
                  </div>
                  {isOpen ? (
                    <CaretUp className="w-5 h-5 text-[#71717A] shrink-0" />
                  ) : (
                    <CaretDown className="w-5 h-5 text-[#71717A] shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                        {/* Objective */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.objective}</h4>
                          <p className="text-[#A1A1AA] text-sm">{proj.objective}</p>
                        </div>
                        {/* Suggested Settings */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.settings_hint}</h4>
                          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2">
                            <code className="font-['JetBrains_Mono'] text-xs text-white">{proj.settings}</code>
                          </div>
                        </div>
                        {/* Tips */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.tips}</h4>
                          <div className="space-y-1.5">
                            {proj.tips.map((tip, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <Star weight="fill" className="w-3 h-3 text-[#F5A524] mt-1 shrink-0" />
                                <p className="text-[#A1A1AA] text-sm">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Deliverables */}
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.deliverables}</h4>
                          <p className="text-white text-sm">{proj.deliverables}</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
